import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge } from 'flowbite-react';
import { Button, Icon, QuoteSignaturePad } from '../../../components';
import PublicQuotesApi from '../../../services/PublicQuotes';
import type { PublicQuoteData, PublicQuoteItem } from '../../../services/PublicQuotes';
import { formatPrice, formatMomentDate } from '../../../locales';

function clientDisplayName(client?: PublicQuoteData['client']): string {
    if (!client)
        return '';
    return [client.firstname, client.lastname].filter(Boolean).join(' ').trim();
}

function itemOriginalLineTotal(item: PublicQuoteItem, quantity: number): number {
    const price = Number(item.price) || 0;
    if (!item.quantityMode)
        return price;
    if (!Number.isFinite(quantity) || quantity <= 0)
        return 0;
    return price * quantity;
}

function itemLineTotal(item: PublicQuoteItem, quantity: number): number {
    if (item.free)
        return 0;
    return itemOriginalLineTotal(item, quantity);
}

function PublicQuoteView() {
    const { token } = useParams();
    const { t, i18n } = useTranslation();

    const [quote, setQuote] = useState<PublicQuoteData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selected, setSelected] = useState<Record<string, boolean>>({});
    const [quantities, setQuantities] = useState<Record<string, number>>({});
    const [signerName, setSignerName] = useState('');
    const [signerRole, setSignerRole] = useState('');
    const [signerCompany, setSignerCompany] = useState('');
    const [signatureImage, setSignatureImage] = useState<string | null>(null);
    const [signatureSource, setSignatureSource] = useState<'draw' | 'upload' | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [doneMessage, setDoneMessage] = useState<string | null>(null);

    const syncFromQuote = (q: PublicQuoteData) => {
        const initialSelected: Record<string, boolean> = {};
        const initialQuantities: Record<string, number> = {};
        (q.items || []).forEach((item) => {
            initialSelected[item._id] = !item.excluded;
            initialQuantities[item._id] = item.quantityMode
                ? (Number(item.quantity) || 0)
                : 1;
        });
        setSelected(initialSelected);
        setQuantities(initialQuantities);
    };

    useEffect(() => {
        if (!token)
            return;
        setLoading(true);
        PublicQuotesApi.getByToken(token)
            .then((q) => {
                setQuote(q);
                syncFromQuote(q);
                setSignerCompany(String(q.client?.company || '').trim());
                setSignerName(clientDisplayName(q.client));
            })
            .catch((err) => {
                console.error(err);
                setError(String(t('quotes.public_not_found') || ''));
            })
            .finally(() => setLoading(false));
    }, [token, t]);

    const quantityOf = (item: PublicQuoteItem) => {
        if (!item.quantityMode)
            return 1;
        if (quote && !quote.canDecide)
            return Number(item.quantity) || 0;
        const q = quantities[item._id];
        return Number.isFinite(q) ? q : (Number(item.quantity) || 0);
    };

    const isItemSelected = (item: PublicQuoteItem) => {
        if (item.quantityMode)
            return quantityOf(item) > 0;
        if (!item.removable)
            return true;
        if (quote && !quote.canDecide)
            return !item.excluded;
        return !!selected[item._id];
    };

    const pricedItems = useMemo(() => {
        if (!quote)
            return [] as PublicQuoteItem[];
        return (quote.items || []).filter((item) => item.price !== 0 || item.free);
    }, [quote]);

    const periodOf = (item: PublicQuoteItem) => item.billingPeriod || 'oneTime';
    const isRecurringPeriod = (period?: string) =>
        period === 'monthly' || period === 'quarterly' || period === 'annual';
    const priceSuffix = (period?: string) => {
        if (period === 'monthly') return '/ Mois';
        if (period === 'quarterly') return '/ Trim';
        if (period === 'annual') return '/ An';
        return '';
    };
    const periodLabelKey = (period?: string) => {
        if (period === 'monthly') return 'quotes.billing_period_label_monthly';
        if (period === 'quarterly') return 'quotes.billing_period_label_quarterly';
        if (period === 'annual') return 'quotes.billing_period_label_annual';
        return 'quotes.billing_period_label_one_time';
    };

    const hasRecurringFees = useMemo(() => {
        if (!quote)
            return false;
        if (quote.hasRecurringFees || quote.hasAnnualFees)
            return true;
        return (quote.items || []).some((item) => isRecurringPeriod(item.billingPeriod));
    }, [quote]);

    const livePeriodTotal = (period: string) => {
        if (!quote)
            return 0;
        return (quote.items || []).reduce((acc, item) => {
            if (periodOf(item) !== period || item.free || !isItemSelected(item))
                return acc;
            return acc + itemLineTotal(item, quantityOf(item));
        }, 0);
    };

    const liveOneTimeTotal = useMemo(
        () => livePeriodTotal('oneTime'),
        [quote, selected, quantities]
    );
    const liveMonthlyTotal = useMemo(
        () => livePeriodTotal('monthly'),
        [quote, selected, quantities]
    );
    const liveQuarterlyTotal = useMemo(
        () => livePeriodTotal('quarterly'),
        [quote, selected, quantities]
    );
    const liveAnnualTotal = useMemo(
        () => livePeriodTotal('annual'),
        [quote, selected, quantities]
    );

    const toggleItem = (item: PublicQuoteItem) => {
        if (!item.removable || !quote?.canDecide || item.quantityMode)
            return;
        setSelected((prev) => ({ ...prev, [item._id]: !prev[item._id] }));
    };

    const changeQuantity = (item: PublicQuoteItem, raw: number) => {
        if (!item.quantityEditable || !quote?.canDecide)
            return;
        const min = Number(item.quantityMin) || 0;
        const max = Number(item.quantityMax) || min;
        let next = Math.floor(Number(raw));
        if (!Number.isFinite(next))
            next = min;
        if (next < min)
            next = min;
        if (next > max)
            next = max;
        setQuantities((prev) => ({ ...prev, [item._id]: next }));
    };

    const accept = async () => {
        if (!token || !quote || submitting)
            return;
        if (!signerName.trim() || !signatureImage || !signatureSource)
            return;

        const acceptedItemIds = (quote.items || [])
            .filter((item) => {
                if (item.quantityMode)
                    return quantityOf(item) > 0;
                return !item.removable || selected[item._id];
            })
            .map((item) => item._id);

        const itemQuantities: Record<string, number> = {};
        (quote.items || []).forEach((item) => {
            if (item.quantityMode)
                itemQuantities[item._id] = quantityOf(item);
        });

        setSubmitting(true);
        try {
            const updated = await PublicQuotesApi.accept(token, {
                acceptedItemIds,
                itemQuantities,
                signerName,
                signerRole,
                signerCompany,
                signatureImage,
                signatureSource,
            });
            setQuote(updated);
            syncFromQuote(updated);
            setDoneMessage(
                updated.partialAcceptance
                    ? String(t('quotes.public_partial_accepted') || '')
                    : String(t('quotes.public_accepted') || '')
            );
            setError(null);
        } catch (err) {
            console.error(err);
            setError(String(t('quotes.public_accept_failed') || ''));
        } finally {
            setSubmitting(false);
        }
    };

    const refuse = async () => {
        if (!token || !quote || submitting)
            return;
        if (!window.confirm(String(t('quotes.public_refuse_confirm') || '')))
            return;

        setSubmitting(true);
        try {
            const updated = await PublicQuotesApi.refuse(token);
            setQuote(updated);
            setDoneMessage(String(t('quotes.public_refused') || ''));
            setError(null);
        } catch (err) {
            console.error(err);
            setError(String(t('quotes.public_refuse_failed') || ''));
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-[60vh] bg-white">
                <Icon type="loading" size={12} />
            </div>
        );
    }

    if (error && !quote) {
        return <div className="p-8 text-center text-red-600 bg-white">{error}</div>;
    }

    if (!quote)
        return null;

    document.title = `Yoda - Devis ${quote.slug}`;

    return (
        <div className="PublicQuoteView max-w-3xl mx-auto px-4 py-8 space-y-8 bg-white text-gray-900">
            <header className="space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-2xl font-bold text-gray-900">{t('quotes.quote_number', { slug: quote.slug })}</h1>
                    <Badge color="info">{t(`quotes.status_type.${quote.status}`)}</Badge>
                </div>
                <h2 className="text-xl font-semibold text-gray-900">{quote.name}</h2>
                {quote.projectName && (
                    <p className="text-gray-600">{t('quotes.quote_project', { project: quote.projectName })}</p>
                )}
                {quote.client?.company && (
                    <p className="text-gray-800 font-medium">{quote.client.company}</p>
                )}
            </header>

            {doneMessage && (
                <div className="rounded-md bg-green-50 border border-green-200 text-green-800 px-4 py-3">
                    {doneMessage}
                </div>
            )}
            {!doneMessage && quote.partialAcceptance && !quote.canDecide && (
                <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3">
                    {t('quotes.public_partial_accepted')}
                </div>
            )}
            {error && quote && (
                <div className="rounded-md bg-red-50 border border-red-200 text-red-700 px-4 py-3">{error}</div>
            )}

            <section
                className="space-y-3 text-md text-gray-800 text-justify [&>p]:m-0"
                dangerouslySetInnerHTML={{
                    __html: (quote.introBody && quote.introBody.trim())
                        ? quote.introBody
                        : [
                            t('quotes.public_intro_greeting'),
                            t('quotes.public_intro_p1'),
                            t('quotes.public_intro_p2'),
                            t('quotes.public_intro_p3'),
                            t('quotes.public_intro_closing'),
                        ].map((p) => `<p>${p}</p>`).join(''),
                }}
            />

            <section className="space-y-3">
                <h3 className="text-lg font-bold text-gray-900">{t('quotes.content')}</h3>
                {(quote.items || []).map((item) => {
                    const qty = quantityOf(item);
                    const checked = isItemSelected(item);
                    const excluded = !checked;
                    const isGroupSeparator = !item.free
                        && Number(item.estimate || 0) === 0
                        && Number(item.price || 0) === 0;

                    if (isGroupSeparator) {
                        return (
                            <div key={item._id} className="pt-5 first:pt-1 space-y-2">
                                <div className="flex items-center gap-3" role="separator">
                                    <div className="flex-1 border-t border-gray-300" aria-hidden />
                                    <h4
                                        className="shrink-0 max-w-[85%] text-center text-sm font-bold uppercase tracking-wide text-gray-700"
                                        dangerouslySetInnerHTML={{ __html: item.title }}
                                    />
                                    <div className="flex-1 border-t border-gray-300" aria-hidden />
                                </div>
                                {item.description && (
                                    <div
                                        className="prose prose-sm max-w-none text-center text-gray-600"
                                        dangerouslySetInnerHTML={{ __html: item.description }}
                                    />
                                )}
                            </div>
                        );
                    }

                    return (
                        <div
                            key={item._id}
                            className={`border rounded-md p-3 bg-white ${
                                excluded
                                    ? 'border-red-200 bg-red-50/40 opacity-75'
                                    : 'border-gray-200'
                            }`}
                        >
                            <div className="flex items-start gap-3">
                                {quote.canDecide && quote.hasRemovableItems && item.removable ? (
                                    <input
                                        type="checkbox"
                                        className="mt-1"
                                        checked={checked}
                                        onChange={() => toggleItem(item)}
                                    />
                                ) : null}
                                <div className="flex-1 min-w-0">
                                    <div className="flex justify-between gap-2">
                                        <div
                                            className={`font-semibold text-gray-900 ${excluded ? 'line-through' : ''}`}
                                            dangerouslySetInnerHTML={{ __html: item.title }}
                                        />
                                        <div className={`whitespace-nowrap font-bold text-gray-900 text-right ${excluded ? 'line-through' : ''}`}>
                                            <span className="flex flex-col items-end text-sm">
                                                <span className="flex flex-row gap-x-2 justify-end">
                                                    {Number(item.estimate) > 0 && (
                                                        <span>{item.estimate} h</span>
                                                    )}
                                                    {item.price !== 0 && (
                                                        item.quantityMode ? (
                                                            <span className="flex flex-col items-end">
                                                                <span>{qty} × {formatPrice(item.price || 0)}</span>
                                                                <span>
                                                                    {formatPrice(itemOriginalLineTotal(item, qty))}
                                                                    {hasRecurringFees ? priceSuffix(periodOf(item)) : ''}
                                                                </span>
                                                            </span>
                                                        ) : (
                                                            <span>
                                                                {formatPrice(item.price || 0)}
                                                                {hasRecurringFees ? priceSuffix(periodOf(item)) : ''}
                                                            </span>
                                                        )
                                                    )}
                                                </span>
                                                {item.price !== 0 && hasRecurringFees && (
                                                    <span className="text-xs font-normal text-gray-600">
                                                        {t(periodLabelKey(periodOf(item)))}
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2 mt-1 items-center">
                                        {excluded && (
                                            <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-800 border border-red-200">
                                                {t('quotes.item_excluded')}
                                            </span>
                                        )}
                                        {item.removable && quote.canDecide && !excluded && (
                                            <span className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100">
                                                {t('quotes.removable_item')}
                                            </span>
                                        )}
                                        {item.free && (
                                            <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700 border border-gray-200">
                                                {t('quotes.free_item')}
                                            </span>
                                        )}
                                        {quote.canDecide && item.quantityEditable ? (
                                            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                                <span>{t('quotes.quantity')}</span>
                                                <input
                                                    type="number"
                                                    className="w-20 rounded-md border-gray-300 bg-white text-gray-900 text-sm"
                                                    min={item.quantityMin}
                                                    max={item.quantityMax}
                                                    step={1}
                                                    value={qty}
                                                    onChange={(e) => changeQuantity(item, Number(e.target.value))}
                                                />
                                            </label>
                                        ) : null}
                                        {item.quantityMode && !item.quantityEditable && (
                                            <span className="text-xs text-gray-600">
                                                {t('quotes.quantity')}: {qty}
                                            </span>
                                        )}
                                    </div>
                                    {item.description && (
                                        <div
                                            className={`prose prose-sm mt-2 max-w-none text-gray-700 ${excluded ? 'line-through opacity-70' : ''}`}
                                            dangerouslySetInnerHTML={{ __html: item.description }}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </section>

            <section className="space-y-2">
                <h3 className="text-lg font-bold text-gray-900">{t('quotes.pricings_resume')}</h3>
                {(() => {
                    const renderPricingTable = (items: PublicQuoteItem[], total: number) => (
                        <table className="w-full border-collapse">
                            <thead>
                                <tr>
                                    <th className="p-2 bg-gray-100 text-sm font-bold text-left border border-gray-200">
                                        {t('quotes.label')}
                                    </th>
                                    <th className="p-2 bg-gray-100 text-sm font-bold text-right border border-gray-200 w-36">
                                        {t('quotes.ht_price')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item) => {
                                    const qty = quantityOf(item);
                                    const excluded = !isItemSelected(item);
                                    const displayTotal = itemOriginalLineTotal(item, qty);
                                    return (
                                        <tr key={item._id} className={excluded ? 'bg-red-50/40 text-gray-500' : ''}>
                                            <td className="p-2 text-sm text-left border border-gray-200">
                                                <div className="flex justify-between gap-2 items-center">
                                                    <span
                                                        className={excluded ? 'line-through' : ''}
                                                        dangerouslySetInnerHTML={{ __html: item.title || '' }}
                                                    />
                                                    <span className="flex items-center gap-2 whitespace-nowrap">
                                                        {item.quantityMode && (
                                                            <span className="text-xs text-gray-600">×{qty}</span>
                                                        )}
                                                        {excluded && (
                                                            <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-800 border border-red-200">
                                                                {t('quotes.item_excluded')}
                                                            </span>
                                                        )}
                                                        {item.free && (
                                                            <span className="font-bold">{t('quotes.free_item')}</span>
                                                        )}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className={`p-2 text-sm text-right border border-gray-200 ${excluded || item.free ? 'line-through' : ''}`}>
                                                {formatPrice(displayTotal)}
                                                {hasRecurringFees ? priceSuffix(periodOf(item)) : ''}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td className="p-2 text-right font-bold border border-gray-200">{t('quotes.total')}</td>
                                    <td className="p-2 text-right font-bold border border-gray-200">
                                        {formatPrice(total)}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    );

                    if (!hasRecurringFees)
                        return renderPricingTable(pricedItems, liveOneTimeTotal);

                    const groups: { period: string; titleKey: string; total: number }[] = [
                        { period: 'oneTime', titleKey: 'quotes.one_time_fees', total: liveOneTimeTotal },
                        { period: 'monthly', titleKey: 'quotes.monthly_fees', total: liveMonthlyTotal },
                        { period: 'quarterly', titleKey: 'quotes.quarterly_fees', total: liveQuarterlyTotal },
                        { period: 'annual', titleKey: 'quotes.annual_fees', total: liveAnnualTotal },
                    ];

                    return (
                        <div className="space-y-4">
                            {groups.map((group) => {
                                const items = pricedItems.filter((item) => periodOf(item) === group.period);
                                if (!items.length)
                                    return null;
                                return (
                                    <div key={group.period}>
                                        <h4 className="text-base font-bold mb-2">{t(group.titleKey)}</h4>
                                        {renderPricingTable(items, group.total)}
                                    </div>
                                );
                            })}
                        </div>
                    );
                })()}
            </section>

            <section className="space-y-2 text-sm text-gray-800 border-t border-gray-300 pt-6">
                <h3 className="text-lg font-bold text-gray-900">{t('quotes.conditions')}</h3>
                {(quote.conditionsBody && quote.conditionsBody.trim()) ? (
                    <div
                        className="space-y-2 [&>p]:m-0"
                        dangerouslySetInnerHTML={{ __html: quote.conditionsBody }}
                    />
                ) : (
                    <>
                        <p>{t('quotes.public_conditions_validity')}</p>
                        <p>
                            {t('quotes.public_conditions_legal_before')}{' '}
                            <a
                                className="text-blue-800 underline"
                                href="https://netoxygen.ch/conditions-generales"
                                target="_blank"
                                rel="noreferrer"
                            >
                                https://netoxygen.ch/conditions-generales
                            </a>
                            {t('quotes.public_conditions_legal_after')}
                        </p>
                    </>
                )}
            </section>

            {quote.canDecide ? (
                <section className="space-y-4 border-t border-gray-300 pt-6">
                    <h3 className="text-lg font-bold text-gray-900">{t('quotes.public_sign_title')}</h3>
                    <p className="font-bold text-gray-900">{t('quotes.public_for_agreement')}</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                {t('quotes.signer_company')}
                            </label>
                            <input
                                className="w-full rounded-md border-gray-300 bg-white text-gray-900"
                                value={signerCompany}
                                onChange={(e) => setSignerCompany(e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                {t('quotes.signer_name')}
                            </label>
                            <input
                                className="w-full rounded-md border-gray-300 bg-white text-gray-900"
                                value={signerName}
                                onChange={(e) => setSignerName(e.target.value)}
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                {t('quotes.signer_role')}
                            </label>
                            <input
                                className="w-full rounded-md border-gray-300 bg-white text-gray-900"
                                value={signerRole}
                                onChange={(e) => setSignerRole(e.target.value)}
                            />
                        </div>
                    </div>
                    <QuoteSignaturePad
                        onChange={({ image, source }) => {
                            setSignatureImage(image);
                            setSignatureSource(source);
                        }}
                    />
                    <div className="flex flex-col sm:flex-row gap-3 justify-end">
                        <Button
                            color="danger"
                            title={t('quotes.public_refuse')}
                            onClick={refuse}
                            loading={submitting}
                        />
                        <Button
                            color="success"
                            title={t('quotes.public_accept')}
                            onClick={accept}
                            loading={submitting}
                            disabled={!signerName.trim() || !signatureImage}
                        />
                    </div>
                </section>
            ) : (
                quote.signature?.signerName && (
                    <div className="border border-gray-200 rounded-md p-4 bg-gray-50 text-gray-900">
                        <div className="font-medium">
                            {t('quotes.signed_by', {
                                name: quote.signature.signerName,
                                date: quote.signature.signedAt
                                    ? formatMomentDate(quote.signature.signedAt, 'LLL', i18n.language)
                                    : '',
                            })}
                        </div>
                    </div>
                )
            )}
        </div>
    );
}

export default PublicQuoteView;
